import { useState } from 'react';
import type { PageSection, SectionsOverride } from '../../../lib/aiJobSummary';
import AiMarkdown from '../AiMarkdown';
import { Badge, Button, Input, Switch } from '../../ui';
import ClientNoteEditor from './ClientNoteEditor';
import SectionHeading, { OpCard } from './SectionHeading';

const label: React.CSSProperties = { fontSize: 'var(--text-xs)', color: 'var(--text-faint)' };
const sourceBox: React.CSSProperties = {
  margin: '8px 0 0',
  padding: '10px 12px',
  borderLeft: '2px solid var(--accent)',
  background: 'var(--surface-input)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-sm)',
  whiteSpace: 'pre-wrap',
  color: 'var(--text-body)',
};

type Draft = { heading: string; purpose: string; source: string; useImage: boolean; imageIdea: string; signature: boolean };

const same = (a: PageSection, b: PageSection) =>
  a.heading === b.heading &&
  (a.purpose ?? '').trim() === (b.purpose ?? '').trim() &&
  (a.message ?? '').trim() === (b.message ?? '').trim() &&
  (a.source ?? '').trim() === (b.source ?? '').trim() &&
  (a.image?.idea ?? '').trim() === (b.image?.idea ?? '').trim() &&
  !!a.signature === !!b.signature;

/**
 * Page Build jobs: the page, section by section, as direction for the build: what each section is, the idea it has
 * to get across, source material it must carry as given, and which sections get a generated image. Each section is
 * editable the way the client note is; saving writes the whole edited page to the spec summary (sections_override),
 * and at build every section goes to its own focused agent with that direction. "Agent's version" puts the spec back.
 */
export default function PageSections({
  sections,
  override,
  readOnly,
  onSave,
}: {
  sections: PageSection[];
  override?: SectionsOverride | null;
  readOnly: boolean;
  /** Saves the edited sections, or null to go back to the agent's. */
  onSave?: (sections: PageSection[] | null) => Promise<void>;
}) {
  const edited = !!override?.sections?.length;
  const shown = edited ? override!.sections : sections;
  const original = new Map(sections.map((s) => [s.id, s]));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>({ heading: '', purpose: '', source: '', useImage: false, imageIdea: '', signature: false });
  const [error, setError] = useState<string | null>(null);
  const canEdit = !readOnly && !!onSave;
  const images = shown.filter((s) => s.image?.idea).length;
  const signatures = shown.filter((s) => s.signature).length;

  const startEdit = (s: PageSection) => {
    setDraft({ heading: s.heading, purpose: s.purpose ?? '', source: s.source ?? '', useImage: !!s.image?.idea, imageIdea: s.image?.idea ?? '', signature: !!s.signature });
    setError(null);
    setEditingId(s.id);
  };

  const saveSection = async (id: string, message: string) => {
    if (!onSave) return;
    if (draft.useImage && !draft.imageIdea.trim()) throw new Error('Say what the image should show, or turn the image off.');
    const next = shown.map((s) =>
      s.id === id
        ? {
            ...s,
            heading: draft.heading.trim() || s.heading,
            purpose: draft.purpose.trim(),
            message,
            source: draft.source.trim() || null,
            image: draft.useImage ? { idea: draft.imageIdea.trim() } : null,
            signature: draft.signature,
          }
        : s,
    );
    await onSave(next);
    setEditingId(null);
  };

  const revert = async () => {
    if (!onSave) return;
    try {
      await onSave(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not go back to the agent’s version.');
    }
  };

  const changed = (s: PageSection) => {
    const o = original.get(s.id);
    return !o || !same(o, s);
  };

  return (
    <section>
      <SectionHeading
        eyebrow={edited ? 'Page Build · your edited version, the build follows it' : 'Page Build · as the agent spec’d it'}
        title="Sections"
        icon="layers"
        count={shown.length}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="t-num" style={label}>
              {images} with an image · {signatures} signature moment{signatures === 1 ? '' : 's'}
            </span>
            {edited ? <Badge tone="accent">edited</Badge> : null}
            {edited && canEdit ? (
              <Button size="sm" variant="ghost" icon="rotate-ccw" onClick={revert}>
                Agent’s version
              </Button>
            ) : null}
          </div>
        }
      />
      <p style={{ margin: '0 0 var(--space-3)', ...label, lineHeight: 'var(--leading-snug)' }}>
        Direction, not finished copy, in the order that moves the buyer to the action. At build, each section goes to its own agent, one after another. The page stays restrained except its signature moments.
      </p>
      {error ? <p style={{ margin: '0 0 8px', fontSize: 'var(--text-sm)', color: 'var(--danger-400)' }}>{error}</p> : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {shown.map((s, i) => {
          const editing = editingId === s.id;
          return (
            <OpCard key={s.id} tone="raised">
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '6px' }}>
                <span className="t-num" style={{ ...label, minWidth: '18px' }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="t-eyebrow" style={{ color: 'var(--text-faint)' }}>
                  H{s.level ?? 2}
                </span>
                {editing ? (
                  <div style={{ flex: 1 }}>
                    <Input fieldSize="sm" value={draft.heading} onChange={(e) => setDraft({ ...draft, heading: e.target.value })} placeholder="Working heading" />
                  </div>
                ) : (
                  <span style={{ flex: 1, fontSize: 'var(--text-md, var(--text-sm))', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'], color: 'var(--text-body)' }}>
                    {s.heading}
                  </span>
                )}
                {s.signature && !editing ? <Badge tone="accent">signature moment</Badge> : null}
                {s.image?.idea && !editing ? <Badge tone="info">image</Badge> : null}
                {s.source && !editing ? <Badge tone="warn">source material</Badge> : null}
                {edited && changed(s) ? <Badge tone="accent">changed</Badge> : null}
                {canEdit && !editing ? (
                  <Button size="sm" variant="ghost" icon="pencil" onClick={() => startEdit(s)}>
                    Edit
                  </Button>
                ) : null}
              </div>

              <div style={{ marginLeft: '28px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {editing ? (
                  <>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={label}>What this section is</span>
                      <Input fieldSize="sm" value={draft.purpose} onChange={(e) => setDraft({ ...draft, purpose: e.target.value })} placeholder="Its job on the page" />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={label}>Source material to include as given (optional)</span>
                      <textarea
                        className="ck-textarea"
                        value={draft.source}
                        rows={draft.source ? 4 : 2}
                        placeholder="Only when the page has to carry specific material: client copy, a quote, a price table. Say where it came from."
                        onChange={(e) => setDraft({ ...draft, source: e.target.value })}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 'var(--text-sm)', resize: 'vertical' }}
                      />
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <Switch checked={draft.signature} onChange={(v) => setDraft({ ...draft, signature: v })} label="Signature moment (the bold one)" />
                      <Switch checked={draft.useImage} onChange={(v) => setDraft({ ...draft, useImage: v })} label="Generated image in this section" />
                      {draft.useImage ? (
                        <div style={{ flex: 1, minWidth: '260px' }}>
                          <Input fieldSize="sm" value={draft.imageIdea} onChange={(e) => setDraft({ ...draft, imageIdea: e.target.value })} placeholder="What the image shows and why it’s there" />
                        </div>
                      ) : null}
                    </div>
                    <span style={label}>What it has to get across</span>
                    <ClientNoteEditor
                      value={s.message ?? ''}
                      wordMax={null}
                      saveLabel="Save section"
                      onSave={(markdown) => saveSection(s.id, markdown)}
                      onCancel={() => setEditingId(null)}
                    />
                  </>
                ) : (
                  <>
                    {s.purpose ? <p style={{ margin: 0, ...label, lineHeight: 'var(--leading-snug)' }}>{s.purpose}</p> : null}
                    <AiMarkdown content={s.message ?? ''} />
                    {s.source ? (
                      <div>
                        <span style={label}>Source material, included as given</span>
                        <div style={sourceBox}>{s.source}</div>
                      </div>
                    ) : null}
                    {s.image?.idea ? (
                      <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                        <span style={label}>Image · </span>
                        {s.image.idea}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            </OpCard>
          );
        })}
      </div>
    </section>
  );
}
